import { useState } from "react";
import { signUp, signIn } from "../lib/supabase";

const D = {
  black:"#0A0A0A", white:"#FFFFFF", red:"#FF0000",
  gray50:"#F7F7F7", gray100:"#EEEEEE", gray200:"#DDDDDD",
  gray400:"#AAAAAA", gray600:"#666666", gray800:"#333333",
  radius:6, radiusSm:4,
};

const field = {
  width:"100%", padding:"13px 14px", fontSize:14, fontWeight:600,
  fontFamily:"'DM Sans',sans-serif", color:D.black,
  background:D.gray50, border:`1.5px solid ${D.gray200}`,
  borderRadius:D.radius, transition:"border-color .15s",
};
const btn = (bg, color, border) => ({
  width:"100%", padding:"15px", borderRadius:D.radius,
  background:bg, color, border: border || "none",
  fontSize:14, fontWeight:800, fontFamily:"'DM Sans',sans-serif",
  cursor:"pointer", letterSpacing:"0.02em", transition:"opacity .12s",
});

export default function AuthScreen({ onSuccess }) {
  const [mode, setMode]         = useState("login");  // "login" | "signup" | "forgot" | "confirm"
  const [name, setName]         = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const reset = () => { setError(""); setName(""); setEmail(""); setPassword(""); setConfirm(""); };

  const handleSubmit = async () => {
    setError("");
    if (!email || !password) { setError("Please fill in all fields."); return; }
    if (mode === "signup" && !name) { setError("Please enter your name."); return; }
    if (mode === "signup" && password !== confirm) { setError("Passwords don't match."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }

    setLoading(true);
    try {
      if (mode === "signup") {
        const data = await signUp({ email, password, name });
        if (data.session) {
          onSuccess(data.user, true);
        } else if (data.user?.email_confirmed_at) {
          // Auto-confirmed but no session yet — sign in immediately
          const signInData = await signIn({ email, password });
          onSuccess(signInData.user, true);
        } else {
          // Email confirmation genuinely required
          setMode("confirm");
          setLoading(false);
          return;
        }
      } else {
        const data = await signIn({ email, password });
        onSuccess(data.user, false);
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Try again.");
    }
    setLoading(false);
  };

  const handleForgot = async () => {
    if (!email) { setError("Enter your email first."); return; }
    setLoading(true);
    try {
      const { supabase } = await import("../lib/supabase");
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) throw error;
    } catch (err) {
      setError(err.message || "Something went wrong. Try again.");
      setLoading(false);
      return;
    }
    setForgotSent(true);
    setLoading(false);
  };

  const focusStyle = `
    input:focus, textarea:focus { outline:none; border-color:#0A0A0A !important; background:#fff !important; }
    .auth-btn:active { opacity:.8; }
    .mode-link { cursor:pointer; text-decoration:underline; color:#0A0A0A; font-weight:700; }
    .mode-link:hover { color:#FF0000; }
    @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
    .fade-up { animation: fadeUp .25s ease both; }
  `;

  return (
    <div style={{maxWidth:430,margin:"0 auto",minHeight:"100vh",background:D.white,fontFamily:"'DM Sans',sans-serif",display:"flex",flexDirection:"column"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{display:none}
        ${focusStyle}
      `}</style>

      {/* Top wordmark bar */}
      <div style={{padding:"52px 28px 0",borderBottom:`1px solid ${D.gray100}`}}>
        <div style={{fontSize:28,fontWeight:900,letterSpacing:"-0.05em",marginBottom:24}}>
          nearme<span style={{color:D.red}}>.</span>
        </div>
      </div>

      {/* ── LOGIN ── */}
      {mode==="login" && (
        <div className="fade-up" style={{flex:1,padding:"32px 28px 40px",display:"flex",flexDirection:"column",gap:0}}>
          <div style={{marginBottom:28}}>
            <div style={{fontSize:22,fontWeight:900,letterSpacing:"-0.04em",marginBottom:4}}>Welcome back</div>
            <div style={{fontSize:13,color:D.gray600,fontWeight:500}}>Sign in to find services near you</div>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
            <input style={field} type="email" placeholder="Email address" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSubmit()}/>
            <input style={field} type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSubmit()}/>
          </div>

          <div style={{textAlign:"right",marginBottom:24}}>
            <span className="mode-link" style={{fontSize:12,fontWeight:600}} onClick={()=>{setMode("forgot");reset();}}>Forgot password?</span>
          </div>

          {error && <div style={{fontSize:12,color:D.red,fontWeight:600,marginBottom:12,padding:"10px 12px",background:"#FFF0F0",borderRadius:D.radiusSm,border:`1px solid #FFCCCC`}}>{error}</div>}

          <button className="auth-btn" onClick={handleSubmit} disabled={loading} style={btn(D.black,D.white)}>
            {loading ? "Signing in…" : "Sign In"}
          </button>

          <div style={{marginTop:16,textAlign:"center",fontSize:13,color:D.gray600,fontWeight:500}}>
            Don't have an account?{" "}
            <span className="mode-link" onClick={()=>{setMode("signup");reset();}}>Sign up</span>
          </div>

        </div>
      )}

      {/* ── SIGN UP ── */}
      {mode==="signup" && (
        <div className="fade-up" style={{flex:1,padding:"32px 28px 40px",display:"flex",flexDirection:"column"}}>
          <div style={{marginBottom:28}}>
            <div style={{fontSize:22,fontWeight:900,letterSpacing:"-0.04em",marginBottom:4}}>Create account</div>
            <div style={{fontSize:13,color:D.gray600,fontWeight:500}}>Find local services and goods near you</div>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:8}}>
            <input style={field} type="text" placeholder="Full name" value={name} onChange={e=>setName(e.target.value)}/>
            <input style={field} type="email" placeholder="Email address" value={email} onChange={e=>setEmail(e.target.value)}/>
            <input style={field} type="password" placeholder="Password (min 6 characters)" value={password} onChange={e=>setPassword(e.target.value)}/>
            <input style={field} type="password" placeholder="Confirm password" value={confirm} onChange={e=>setConfirm(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSubmit()}/>
          </div>

          <div style={{fontSize:11,color:D.gray400,fontWeight:500,lineHeight:1.5,marginBottom:20}}>
            By signing up you agree to our{" "}
            <span style={{color:D.black,fontWeight:700,textDecoration:"underline",cursor:"pointer"}}>Terms of Service</span>
            {" "}and{" "}
            <span style={{color:D.black,fontWeight:700,textDecoration:"underline",cursor:"pointer"}}>Privacy Policy</span>.
          </div>

          {error && <div style={{fontSize:12,color:D.red,fontWeight:600,marginBottom:12,padding:"10px 12px",background:"#FFF0F0",borderRadius:D.radiusSm,border:`1px solid #FFCCCC`}}>{error}</div>}

          <button className="auth-btn" onClick={handleSubmit} disabled={loading} style={btn(D.black,D.white)}>
            {loading ? "Creating account…" : "Create Account"}
          </button>

          <div style={{marginTop:16,textAlign:"center",fontSize:13,color:D.gray600,fontWeight:500}}>
            Already have an account?{" "}
            <span className="mode-link" onClick={()=>{setMode("login");reset();}}>Sign in</span>
          </div>
        </div>
      )}

      {/* ── CONFIRM EMAIL ── */}
      {mode==="confirm" && (
        <div className="fade-up" style={{flex:1,padding:"32px 28px 40px",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",textAlign:"center"}}>
          <div style={{fontSize:52,marginBottom:20}}>📬</div>
          <div style={{fontSize:22,fontWeight:900,letterSpacing:"-0.04em",marginBottom:8}}>Check your email</div>
          <div style={{fontSize:13,color:D.gray600,lineHeight:1.6,marginBottom:32}}>
            We sent a confirmation link to <strong>{email}</strong>. Click it to activate your account and you'll be taken right in.
          </div>
          <div style={{fontSize:12,color:D.gray400,fontWeight:500,marginBottom:24}}>Didn't get it? Check your spam folder.</div>
          <span className="mode-link" style={{fontSize:13}} onClick={()=>{setMode("login");reset();}}>← Back to sign in</span>
        </div>
      )}

      {/* ── FORGOT PASSWORD ── */}
      {mode==="forgot" && (
        <div className="fade-up" style={{flex:1,padding:"32px 28px 40px",display:"flex",flexDirection:"column"}}>
          {!forgotSent ? (<>
            <div style={{marginBottom:28}}>
              <div style={{fontSize:22,fontWeight:900,letterSpacing:"-0.04em",marginBottom:4}}>Reset password</div>
              <div style={{fontSize:13,color:D.gray600,fontWeight:500}}>Enter your email and we'll send a reset link</div>
            </div>
            <input style={{...field,marginBottom:16}} type="email" placeholder="Email address" value={email} onChange={e=>setEmail(e.target.value)}/>
            {error && <div style={{fontSize:12,color:D.red,fontWeight:600,marginBottom:12,padding:"10px 12px",background:"#FFF0F0",borderRadius:D.radiusSm}}>{error}</div>}
            <button className="auth-btn" onClick={handleForgot} disabled={loading} style={btn(D.black,D.white)}>
              {loading ? "Sending…" : "Send Reset Link"}
            </button>
            <div style={{marginTop:16,textAlign:"center"}}>
              <span className="mode-link" style={{fontSize:13}} onClick={()=>{setMode("login");reset();}}>← Back to sign in</span>
            </div>
          </>) : (
            <div style={{textAlign:"center",paddingTop:40}}>
              <div style={{fontSize:44,marginBottom:16}}>📬</div>
              <div style={{fontSize:18,fontWeight:900,letterSpacing:"-0.03em",marginBottom:8}}>Check your email</div>
              <div style={{fontSize:13,color:D.gray600,lineHeight:1.6,marginBottom:28}}>We sent a password reset link to <strong>{email}</strong>. Check your inbox and follow the link.</div>
              <span className="mode-link" style={{fontSize:13}} onClick={()=>{setMode("login");reset();setForgotSent(false);}}>← Back to sign in</span>
            </div>
          )}
        </div>
      )}

      {/* Bottom campus tag */}
      <div style={{padding:"16px 28px",borderTop:`1px solid ${D.gray100}`,display:"flex",alignItems:"center",gap:6}}>
        <div style={{width:6,height:6,borderRadius:"50%",background:D.red}}/>
        <span style={{fontSize:11,fontWeight:700,color:D.gray600,letterSpacing:"0.04em"}}>NEARME. · LOCAL SERVICES & GOODS</span>
      </div>
    </div>
  );
}
